// UserDetails/ContactInfo.jsx

import React, { useState } from "react";

export default function ContactInfo({ data, onUpdate, onUpdateContact }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    email: data.email || "",
    phone: data.phone || "",
    address: data.address || {},
    fullAddress: data.fullAddress || "",
  });
  const [saveError, setSaveError] = useState(null);

  const handleEdit = () => {
    setIsEditing(true);
    setEditData({
      email: data.email || "",
      phone: data.phone || "",
      address: data.address || {},
      fullAddress: data.fullAddress || "",
    });
  };

  const handleSave = async () => {
    try {
      setSaveError(null);
      const contactUpdate = {};
      const profileUpdate = {};

      if (editData.email !== data.email) contactUpdate.email = editData.email;
      if (editData.phone !== data.phone) contactUpdate.phone = editData.phone;

      if (editData.fullAddress !== data.fullAddress)
        profileUpdate.fullAddress = editData.fullAddress;
      if (JSON.stringify(editData.address) !== JSON.stringify(data.address))
        profileUpdate.address = editData.address;

      // First call contact API only if email/phone changed
      if (Object.keys(contactUpdate).length > 0 && onUpdateContact) {
        await onUpdateContact(contactUpdate);
      }
      // Then call general profile update for address/fullAddress
      if (Object.keys(profileUpdate).length > 0) {
        await onUpdate(profileUpdate);
      }
      setIsEditing(false);
    } catch (error) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message || error?.message || "";
      if (status === 400) {
        const attemptedEmail = editData.email !== data.email;
        const attemptedPhone = editData.phone !== data.phone;
        if (attemptedPhone && !attemptedEmail) setSaveError("This phone number is already in use.");
        else if (attemptedEmail && !attemptedPhone) setSaveError("This email is already in use.");
        else setSaveError(message || "Email or phone already in use");
      } else {
        setSaveError("Failed to update contact info.");
      }
      console.error("Failed to update contact info:", error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSaveError(null);
    setEditData({
      email: data.email || "",
      phone: data.phone || "",
      address: data.address || {},
      fullAddress: data.fullAddress || "",
    });
  };

  const handleInputChange = (field, value) => {
    if (field === "address") {
      setEditData((prev) => ({
        ...prev,
        address: { ...prev.address, ...value },
      }));
    } else {
      setEditData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const formatAddress = (address) => {
    if (!address) return "";
    return [
      address.street,
      address.landmark && `Near ${address.landmark}`,
      address.city,
      address.state,
      address.pincode,
    ]
      .filter(Boolean)
      .join(", ");
  };

  if (isEditing) {
    return (
      <div className="mt-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">
            Contact Information
          </h3>
          <div className="flex space-x-2">
            <button
              onClick={handleSave}
              className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              Save
            </button>
            <button
              onClick={handleCancel}
              className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium"
            >
              Cancel
            </button>
          </div>
        </div>

        {saveError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {saveError}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={editData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter email address"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              value={editData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter phone number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Address (Alternative)
            </label>
            <textarea
              value={editData.fullAddress}
              onChange={(e) => handleInputChange("fullAddress", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter full address"
              rows="3"
            />
          </div>

          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">
              Detailed Address
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Street
                </label>
                <input
                  type="text"
                  value={editData.address.street || ""}
                  onChange={(e) =>
                    handleInputChange("address", { street: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Street address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Landmark
                </label>
                <input
                  type="text"
                  value={editData.address.landmark || ""}
                  onChange={(e) =>
                    handleInputChange("address", { landmark: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Near landmark"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  City
                </label>
                <input
                  type="text"
                  value={editData.address.city || ""}
                  onChange={(e) =>
                    handleInputChange("address", { city: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="City"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  State
                </label>
                <input
                  type="text"
                  value={editData.address.state || ""}
                  onChange={(e) =>
                    handleInputChange("address", { state: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="State"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Pincode
                </label>
                <input
                  type="text"
                  value={editData.address.pincode || ""}
                  onChange={(e) =>
                    handleInputChange("address", { pincode: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Pincode"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800">
          Contact Information
        </h3>
        <button
          onClick={handleEdit}
          className="px-3 py-1 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors text-xs font-medium"
        >
          ✏️ Edit
        </button>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-gray-500">✉</span>
          <span className="font-medium">
            {data.email || "Email Not provided"}
          </span>
          {data.email && data.emailVerified && (
            <span className="ml-2 px-2 py-1 text-xs rounded bg-green-100 text-green-800 font-semibold">
              Verified
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-500">📱</span>
          <span className="font-medium">{data.phone}</span>
          {data.phoneVerified && (
            <span className="ml-2 px-2 py-1 text-xs rounded bg-green-100 text-green-800 font-semibold">
              Verified
            </span>
          )}
        </div>
        {(data.address || data.fullAddress) && (
          <div className="flex items-center gap-2">
            <span className="text-gray-500">📍</span>
            <span className="font-medium">
              {data.address ? formatAddress(data.address) : data.fullAddress}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
